import { useCallback, useRef } from 'react'

export interface HeightManagerProps {
    setCacheHeight?: (cardHeight: number, rowIndex: number) => void
    clearCacheRow?: (rowIndex: number) => void
    rowIndex?: number
    measure?: VoidFunction
    totalChanges?: number
}

export interface HeightManagerReturn {
    cardRef: React.RefObject<HTMLElement>
    handleHeightChange: () => void
    handleShowMoreToggle: () => void
    measureHeight: () => void
}

export const useCardHeightManager = ({
    setCacheHeight,
    clearCacheRow,
    rowIndex,
    measure,
    totalChanges,
}: HeightManagerProps): HeightManagerReturn => {
    const cardRef = useRef<HTMLElement>(null)
    const measureTimeoutRef = useRef<NodeJS.Timeout>()

    const measureHeight = useCallback(() => {
        if (!cardRef.current || typeof rowIndex !== 'number') {
            if (measure) {
                requestAnimationFrame(measure)
            }
            return
        }

        const height = cardRef.current.offsetHeight
        if (height > 0 && setCacheHeight) {
            setCacheHeight(height, rowIndex)
        }

        if (measure) {
            measure()
        }
    }, [setCacheHeight, rowIndex, measure])

    const handleHeightChange = useCallback(() => {
        // Clear any pending measurements
        if (measureTimeoutRef.current) {
            clearTimeout(measureTimeoutRef.current)
        }

        // Clear cache for current row
        if (typeof rowIndex === 'number' && clearCacheRow) {
            clearCacheRow(rowIndex)
        }

        // Use double requestAnimationFrame for reliable DOM measurement
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                measureHeight()
            })
        })
    }, [clearCacheRow, rowIndex, measureHeight])

    const handleShowMoreToggle = useCallback(() => {
        // Clear cache for current row and all subsequent rows
        if (typeof rowIndex === 'number' && clearCacheRow) {
            const maxRows = totalChanges || rowIndex + 1
            for (let i = rowIndex; i < maxRows; i++) {
                clearCacheRow(i)
            }
        }

        if (measure) {
            measure()
        }

        // Schedule delayed measurement with cleanup
        measureTimeoutRef.current = setTimeout(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    measureHeight()
                    // Final measurement to ensure layout is complete
                    measureTimeoutRef.current = setTimeout(() => {
                        if (measure) {
                            measure()
                        }
                    }, 10)
                })
            })
        }, 50)
    }, [clearCacheRow, rowIndex, totalChanges, measure, measureHeight])

    return {
        cardRef,
        handleHeightChange,
        handleShowMoreToggle,
        measureHeight,
    }
}
